import { useCallback } from 'react';
import { isFunction } from 'lodash';
import { ToastAlert, useToast } from '@frameio/vapor';
import { EMPTY_OBJECT } from '../../constants';
import { ToastButtons } from './ToastButtons';

type UseToastTryCatchHandlerArguments = {
  duration?: number | null;
};

type TryCatchCallbackSuccessArgs = Pick<
  TryCatchCallbackArgs,
  | 'buttonCallback'
  | 'buttonLabel'
  | 'secondaryButtonCallback'
  | 'secondaryButtonLabel'
  | 'successMessage'
>;

type TryCatchCallbackArgs<
  SuccessResponseType extends any = any,
  ErrorMessageResponseType extends any = any,
> = {
  attempt: (...args: any[]) => Promise<any>;
  adornmentBefore?: React.ReactNode;
  buttonCallback?: (response: SuccessResponseType) => void;
  buttonLabel?: string;
  errorMessage?: ((error: ErrorMessageResponseType) => string) | string | false;
  overwriteOptionsOnSuccess?: (
    response: SuccessResponseType
  ) => TryCatchCallbackSuccessArgs | undefined;
  secondaryButtonCallback?: (response: SuccessResponseType) => void;
  secondaryButtonLabel?: string;
  successMessage?: ((response: SuccessResponseType) => string) | string | false;
};

export function useToastTryCatchHandler(
  { duration }: UseToastTryCatchHandlerArguments = { duration: 2000 }
) {
  const { addToast } = useToast();

  return useCallback(
    async function tryCatchOnConfirm<T = any, E = any>({
      attempt,
      adornmentBefore,
      errorMessage,
      overwriteOptionsOnSuccess,
      ...successOptions
    }: TryCatchCallbackArgs<T, E>) {
      try {
        const response = await attempt();

        /**
         * @note This is NOT an ideal interface for handling altering Toast
         * options based on the data response. A better refactor would be
         * changing this hook to accept either a Promise or an already-resolved
         * object and rendering a success or error Toast depending on whether
         * the data passed in was an Error object.
         */
        const newOpts = (overwriteOptionsOnSuccess?.(response) ||
          EMPTY_OBJECT) as TryCatchCallbackSuccessArgs;

        /**
         * Override success-related props with any options incoming that were
         * set as a result of the response. One use case would be if the
         * response is successful but the type of success no longer necessitates
         * the use of a button to undo the action
         */
        const {
          buttonCallback,
          buttonLabel,
          secondaryButtonCallback,
          secondaryButtonLabel,
          successMessage,
        } = {
          ...successOptions,
          ...newOpts,
        };

        if (successMessage) {
          addToast(
            (closeToast, toastCloseLabel) => (
              <ToastAlert
                adornmentAfter={
                  <ToastButtons
                    buttonCallback={buttonCallback}
                    buttonLabel={buttonLabel}
                    onClose={closeToast}
                    response={response}
                    secondaryButtonCallback={secondaryButtonCallback}
                    secondaryButtonLabel={secondaryButtonLabel}
                  />
                }
                adornmentBefore={adornmentBefore}
                closeLabel={toastCloseLabel}
                onRequestClose={closeToast}
                title={
                  isFunction(successMessage)
                    ? successMessage(response)
                    : successMessage
                }
                variant="success"
              />
            ),
            { duration }
          );
        }

        return response;
      } catch (error) {
        if (errorMessage) {
          const title = isFunction(errorMessage)
            ? errorMessage(error as any)
            : errorMessage;
          addToast(
            (closeToast, toastCloseLabel) => (
              <ToastAlert
                closeLabel={toastCloseLabel}
                onRequestClose={closeToast}
                title={title}
                variant="error"
              />
            ),
            { duration }
          );
        }

        return error;
      }
    },
    [addToast, duration]
  );
}
